import TagAssignmentSheet, {
  TagEntityType,
} from "@/components/Tags/TagAssignmentSheet";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader } from "@/components/ui/card";
import { PatientHoverCard } from "@/pages/Facility/services/serviceRequests/PatientHoverCard";
import {
  PatientInsuranceRead,
  PatientListRead,
  PatientRead,
  PublicPatientRead,
} from "@/types/emr/patient/patient";
import {
  getTagHierarchyDisplay,
  TagConfig,
} from "@/types/emr/tagConfig/tagConfig";
import { SettingsIcon } from "lucide-react";
import { Link } from "raviger";
import { useTranslation } from "react-i18next";

import { getPermissions } from "@/common/Permissions";
import { canUseFullPatientActions } from "@/components/Patient/patientActionVisibility";
import { usePermissions } from "@/context/PermissionContext";
import useAuthUser from "@/hooks/useAuthUser";
import useCurrentFacility from "@/pages/Facility/utils/useCurrentFacility";

const getCompletionPercentage = (values: unknown[]) => {
  if (!values.length) return 0;

  const filledCount = values.filter((value) => {
    if (typeof value === "boolean") return true;
    if (typeof value === "number") return Number.isFinite(value);
    if (typeof value === "string") return value.trim().length > 0;
    if (Array.isArray(value)) return value.length > 0;
    return value !== null && value !== undefined;
  }).length;

  return Math.round((filledCount / values.length) * 100);
};

// Insurance records are validated before they can be saved.
// Therefore, the presence of a saved insurance record implies
// that all required insurance fields have been completed.
//
// Insurance progress is considered complete only when the patient has
// at least one active insurance record. Since the backend does not expose
// an explicit active flag, active status is derived from policy_validity_date.
//
// Compare YYYY-MM-DD date strings directly to avoid timezone issues that can
// occur when parsing date-only strings with JavaScript Date.
const getInsuranceCompletionPercentage = (
  records: PatientInsuranceRead[] = [],
) => {
  const today = new Date();
  const localToday = [
    today.getFullYear(),
    String(today.getMonth() + 1).padStart(2, "0"),
    String(today.getDate()).padStart(2, "0"),
  ].join("-");

  const hasActiveInsurance = records.some(
    (record) =>
      record.policy_validity_date && record.policy_validity_date >= localToday,
  );

  return hasActiveInsurance ? 100 : 0;
};

function PatientDetailsProgressCard({
  patient,
  facilityId,
}: {
  patient: PublicPatientRead | PatientListRead | PatientRead;
  facilityId: string;
}) {
  const { t } = useTranslation();
  const { hasPermission } = usePermissions();
  const { facility } = useCurrentFacility();
  const facilityPermissions = getPermissions(
    hasPermission,
    facility?.permissions ?? [],
  );
  const user = useAuthUser();
  const canEditPatientInWorkspace = canUseFullPatientActions({
    isPractitionerWorkspace: facilityPermissions.canAccessPractitionerWorkspace,
    canAccessNursingActions: facilityPermissions.canAccessNursingActions,
    isSuperuser: user?.is_superuser,
  });
  const isFullPatient = "instance_identifiers" in patient;

  const sections = [
    {
      label: t("patient_basics"),
      percentage: getCompletionPercentage([
        patient.name,
        patient.gender,
        patient.phone_number,
        patient.date_of_birth || patient.year_of_birth,
        "relationship_type" in patient ? patient.relationship_type : undefined,
        "relationship_name" in patient ? patient.relationship_name : undefined,
        "marital_status" in patient ? patient.marital_status : undefined,
      ]),
      colorClass: "bg-teal-500",
    },
    {
      label: t("additional_details"),
      percentage: getCompletionPercentage([
        patient.address,
        "permanent_address" in patient ? patient.permanent_address : undefined,
        patient.pincode,
        patient.emergency_phone_number,
        patient.blood_group,
        "geo_organization" in patient ? patient.geo_organization : undefined,
        "instance_tags" in patient ? patient.instance_tags : undefined,
        isFullPatient ? patient.instance_identifiers : undefined,
        isFullPatient ? patient.facility_identifiers : undefined,
      ]),
      colorClass: "bg-amber-400",
    },
    {
      label: t("insurance_details"),
      percentage: getInsuranceCompletionPercentage(
        isFullPatient ? patient.insurance_records : [],
      ),
      colorClass: "bg-orange-400",
    },
  ];

  return (
    <Card className="mx-3 rounded-md rounded-t-none border border-gray-200 bg-white shadow-sm">
      <CardHeader className="px-3 py-2">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
          <div className="grid flex-1 grid-cols-1 gap-3 md:grid-cols-3">
            {sections.map((section) => (
              <div key={section.label} className="min-w-0">
                <div className="mb-1 flex items-center justify-between gap-3 text-[11px] font-semibold text-gray-700">
                  <span className="truncate">{section.label}</span>
                  <span className="shrink-0 text-gray-600">
                    {section.percentage}%
                  </span>
                </div>
                <div className="h-1.5 overflow-hidden rounded-full bg-gray-200">
                  <div
                    className={`h-full rounded-full ${section.colorClass}`}
                    style={{ width: `${section.percentage}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
          {facilityPermissions.canWritePatient && canEditPatientInWorkspace && (
            <Link
              href={`/facility/${facilityId}/patient/${patient.id}/update`}
              className="self-end text-xs font-semibold text-gray-900 underline lg:self-center"
            >
              {t("update")}
            </Link>
          )}
        </div>
      </CardHeader>
    </Card>
  );
}

export const PatientInfoCard = ({
  patient,
  tags,
  facilityId,
  onTagsUpdate,
  children,
  tagEntityType,
  tagEntityId,
}: {
  patient: PublicPatientRead | PatientListRead | PatientRead;
  tags: TagConfig[];
  facilityId: string;
  onTagsUpdate: () => void;
  children?: React.ReactNode;
  tagEntityType: TagEntityType;
  tagEntityId: string;
}) => {
  const { t } = useTranslation();

  return (
    <>
      <Card className="bg-white shadow-sm rounded-md">
        <CardHeader className="pb-4 flex flex-col sm:flex-row md:flex-wrap lg:flex-nowrap sm:items-center justify-between px-2">
          <div className="space-y-4">
            <PatientHoverCard patient={patient} facilityId={facilityId} />
          </div>
          {children}
        </CardHeader>
      </Card>
      <Card className="bg-white shadow-sm mx-3 rounded-md rounded-t-none rounded-b-md">
        <CardHeader className="px-1 py-0 pt-2 md:pt-1">
          <div className="flex flex-col md:flex-row items-center justify-between gap-2">
            <div className="flex flex-wrap items-center gap-2">
              {tags.length > 0 ? (
                tags.map((t) => (
                  <Badge key={t.id} variant="outline">
                    {getTagHierarchyDisplay(t)}
                  </Badge>
                ))
              ) : (
                <span className="text-sm text-gray-500 px-2">
                  {t("no_tags_assigned")}
                </span>
              )}
            </div>

            <TagAssignmentSheet
              entityType={tagEntityType}
              entityId={tagEntityId}
              facilityId={facilityId}
              currentTags={tags}
              canWrite={true}
              onUpdate={() => {
                onTagsUpdate();
              }}
              trigger={
                <Button variant="ghost">
                  <SettingsIcon className=" text-gray-950" strokeWidth={1.5} />
                  <span className="font-semibold underline">
                    {tags.length === 0 ? t("add_tags") : t("manage_tags")}
                  </span>
                </Button>
              }
            />
          </div>
        </CardHeader>
      </Card>
      {tagEntityType === "patient" && (
        <PatientDetailsProgressCard patient={patient} facilityId={facilityId} />
      )}
    </>
  );
};
